'use client';

import { createContext, useMemo } from 'react';
import type { CSSProperties, PropsWithChildren } from 'react';

import type { DraggableSyntheticListeners } from '@dnd-kit/core';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import classnames from 'classnames';
import { MdDragIndicator } from 'react-icons/md';

import type { BaseComponentProps } from '../../../base-component.types';
import { IconButton } from '../../../icon-button';

import styles from './SortableItem.module.scss';

type ContainerProps = BaseComponentProps & {
  id: string | number;
};

interface Context {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  attributes: Record<string, any>;
  listeners: DraggableSyntheticListeners;
  ref(node: HTMLElement | null): void;
}

const SortableItemContext = createContext<Context>({
  attributes: {},
  listeners: undefined,
  ref() {},
});

export function SortableItem({
  style,
  className,
  children,
  id,
}: PropsWithChildren<ContainerProps>) {
  const {
    attributes,
    isDragging,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
  } = useSortable({ id });
  const context = useMemo(
    () => ({
      attributes,
      listeners,
      ref: setActivatorNodeRef,
    }),
    [attributes, listeners, setActivatorNodeRef],
  );

  const draggingStyle: CSSProperties = {
    opacity: isDragging ? 0.4 : undefined,
    transform: CSS.Translate.toString(transform),
    transition,
  };

  return (
    <SortableItemContext.Provider value={context}>
      <li
        style={{ ...draggingStyle, ...style }}
        className={classnames(styles['sortable-item'], className)}
        ref={setNodeRef}
      >
        {children}
        <IconButton
          className={styles['drag-handle']}
          size="sm"
          {...attributes}
          {...listeners}
          ref={setActivatorNodeRef}
          icon={<MdDragIndicator />}
          aria-label="Reordenar"
        />
      </li>
    </SortableItemContext.Provider>
  );
}
